import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useConfig } from "../hooks/useConfig";
import { getProductTerminology } from "../constants/product-terminology";
import { toast } from "react-toastify";
import {
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationMutation,
  useGetNotificationStatsQuery,
  notificationApi,
} from "../redux/services/notifications/notificationApi";
import { useDispatch } from "react-redux";
import { createSocketClient } from "../utils/socketAuth";

const NotificationContext = createContext();

/**
 * Format notification into a human-readable toast message
 * Uses human IDs (loadNumber, invoiceNumber, etc.) instead of MongoDB ObjectIds
 * @param {Object} notification - Notification object
 * @returns {string} Formatted toast message
 */
const formatNotificationToast = (notification, terms) => {
  const metadata = notification?.metadata || {};
  const title = notification?.title || "";
  const message = notification?.message || "";
  const jobLabel = terms?.job || "Job";
  const legLabel = terms?.leg || "Leg";

  switch (notification?.type) {
    case "CHAT": {
      // Chat notifications: show sender name with context (Job #123, etc.)
      const senderName = metadata.senderName || title || "New message";
      const preview = metadata.preview || message || "";
      const context = metadata.context;

      let contextLabel = "";
      if (context?.humanId && context?.entityType) {
        const displayType =
          context.entityType === "job"
            ? jobLabel
            : context.entityType === "leg"
            ? legLabel
            : context.entityType === "manifest"
            ? "Manifest"
            : context.entityType === "payout"
            ? "Payout"
            : context.entityType === "invoice"
            ? "Invoice"
            : context.entityType;
        contextLabel = ` (${displayType} #${context.humanId})`;
      }

      return `💬 ${senderName}${contextLabel}\n${preview}`;
    }

    case "DISPATCH": {
      // Dispatch notifications: show load/job number
      const loadNumber = metadata.loadNumber || metadata.jobNumber || "";
      if (loadNumber) {
        return `🚚 ${title}\nLoad #${loadNumber}${
          message ? `\n${message}` : ""
        }`;
      }
      return `🚚 ${title}${message ? `\n${message}` : ""}`;
    }

    case "INVOICE": {
      // Invoice notifications: show invoice number
      const invoiceNumber = metadata.invoiceNumber || "";
      if (invoiceNumber) {
        return `📄 ${title}\nInvoice #${invoiceNumber}${
          message ? `\n${message}` : ""
        }`;
      }
      return `📄 ${title}${message ? `\n${message}` : ""}`;
    }

    case "SERVICE_REQUEST": {
      // Service request notifications: show request number or work order
      const requestNumber =
        metadata.requestNumber || metadata.workOrderNumber || "";
      if (requestNumber) {
        return `🔧 ${title}\nRequest #${requestNumber}${
          message ? `\n${message}` : ""
        }`;
      }
      return `🔧 ${title}${message ? `\n${message}` : ""}`;
    }

    case "MAINTENANCE": {
      // Maintenance notifications
      const maintenanceNumber =
        metadata.maintenanceNumber || metadata.workOrderNumber || "";
      if (maintenanceNumber) {
        return `🛠️ ${title}\nWork Order #${maintenanceNumber}${
          message ? `\n${message}` : ""
        }`;
      }
      return `🛠️ ${title}${message ? `\n${message}` : ""}`;
    }

    case "EMERGENCY": {
      // Emergency notifications - high visibility
      return `🚨 ${title}${message ? `\n${message}` : ""}`;
    }

    case "SYSTEM": {
      // System notifications
      return `ℹ️ ${title}${message ? `\n${message}` : ""}`;
    }

    default:
      // Generic fallback
      return message || title;
  }
};

export const NotificationProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const { user } = useAuth();
  const { appType } = useConfig();
  const terms = getProductTerminology(appType);
  const termsRef = useRef(terms);
  termsRef.current = terms;
  const dispatch = useDispatch();

  // RTK Query hooks
  const {
    data: notificationsData,
    error: notificationsError,
    isLoading: notificationsLoading,
    refetch: refetchNotifications,
  } = useGetNotificationsQuery(
    { page: 1, limit: 50 }, // Get first 50 notifications
    {
      skip: !user,
      // Keep badge + list fresh even if socket misses events.
      pollingInterval: 10000,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );

  const { data: statsData, refetch: refetchStats } =
    useGetNotificationStatsQuery(undefined, {
      skip: !user,
      pollingInterval: 10000,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    });

  // Mutations
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  // Socket.IO for real-time updates
  useEffect(() => {
    if (user) {
      const newSocket = createSocketClient();

      // Handle connection
      newSocket.on("connect", () => {
        // console.log(
        //   "[NotifyClient] Connected to notification service, id:",
        //   newSocket.id
        // );
      });

      newSocket.on("connect_error", (err) => {
        console.warn("[NotifyClient] connect_error:", err?.message);
      });

      newSocket.on("error", (err) => {
        console.warn("[NotifyClient] error:", err?.message);
      });

      newSocket.on("disconnect", (reason) => {
        console.log("[NotifyClient] disconnected:", reason);
      });

      // Listen for new notifications
      newSocket.on("notification:new", (notification) => {
        // Invalidate cache to refetch notifications
        dispatch(
          notificationApi.util.invalidateTags([
            { type: "Notifications", id: "LIST" },
            { type: "Notifications", id: "STATS" },
          ])
        );

        // Format toast message with human-readable IDs based on notification type
        const toastMessage = formatNotificationToast(
          notification,
          termsRef.current
        );

        // Show toast notification with site brand colors
        // Use notification ID to prevent duplicates
        toast(toastMessage, {
          toastId: notification._id || `notification-${Date.now()}`, // Prevent duplicate toasts
          type: getToastType(notification.type, notification.priority),
          position: "top-right",
          autoClose: getAutoCloseTime(notification.priority),
          style: { whiteSpace: "pre-line" }, // Allow line breaks in messages
        });
      });

      // Listen for notification updates (read status, etc.)
      newSocket.on("notification:updated", (notification) => {
        // Invalidate specific notification and list
        dispatch(
          notificationApi.util.invalidateTags([
            { type: "Notifications", id: notification._id },
            { type: "Notifications", id: "LIST" },
            { type: "Notifications", id: "STATS" },
          ])
        );
      });

      // Listen for notification deletions
      newSocket.on("notification:deleted", (notificationId) => {
        // Invalidate specific notification and list
        dispatch(
          notificationApi.util.invalidateTags([
            { type: "Notifications", id: notificationId },
            { type: "Notifications", id: "LIST" },
            { type: "Notifications", id: "STATS" },
          ])
        );
      });

      setSocket(newSocket);

      // Cleanup on unmount
      return () => newSocket.close();
    }
  }, [user, dispatch]);

  /**
   * Map notification type and priority to toast type
   * Uses site brand colors: success (primary teal), warning (amber), error (red), info (blue)
   */
  const getToastType = (notificationType, priority) => {
    if (priority === "urgent" || notificationType === "EMERGENCY")
      return "error";
    if (priority === "high" || notificationType === "DISPATCH")
      return "warning";
    if (notificationType === "SYSTEM") return "info";
    return "success"; // Primary teal for general success/info
  };

  const getAutoCloseTime = (priority) => {
    if (priority === "urgent") return 10000; // 10 seconds
    if (priority === "high") return 7000; // 7 seconds
    return 5000; // 5 seconds default
  };

  // Enhanced functions using RTK Query
  const markNotificationAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId).unwrap();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      await markAllAsRead().unwrap();
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      toast.error("Failed to mark all notifications as read");
    }
  };

  const deleteNotificationById = async (notificationId) => {
    try {
      await deleteNotification(notificationId).unwrap();
      toast.success("Notification deleted");
    } catch (error) {
      console.error("Failed to delete notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  const value = {
    // Data from RTK Query
    notifications: notificationsData?.data?.notifications || [],
    pagination: notificationsData?.data?.pagination,
    stats: statsData?.data,

    // Loading states
    isLoading: notificationsLoading,
    error: notificationsError,

    // Actions
    markAsRead: markNotificationAsRead,
    markAllAsRead: markAllNotificationsAsRead,
    deleteNotification: deleteNotificationById,
    refetchNotifications,
    refetchStats,

    // Socket connection status
    isConnected: socket?.connected || false,

    // Legacy compatibility
    clearNotifications: () => {
      // This can be implemented if needed for backwards compatibility
      console.warn(
        "clearNotifications is deprecated, use RTK Query refetch instead"
      );
      refetchNotifications();
    },
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
